import { useState } from "react";
import { useReveal } from "@/hooks/useReveal";
import { sendContactForm } from "@/lib/sendContactForm";

export function Contact({ onResumeClick }) {
  const revealRef = useReveal();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Please fill in your name, email, and message.");
      return;
    }

    setStatus("sending");
    const result = await sendContactForm({
      name: form.name.trim(),
      email: form.email.trim(),
      message: form.message.trim(),
      source: "homepage-contact",
    });

    if (result.ok) {
      setStatus("sent");
      setForm({ name: "", email: "", message: "" });
    } else {
      setError(result.error);
      setStatus("idle");
    }
  };

  return (
    <section
      id="contact"
      ref={revealRef}
      className="stack-mobile relative z-[2] grid gap-12 px-6 pb-[90px] pt-8 md:grid-cols-2 md:px-14"
    >
      <div className="rounded-3xl border-3 border-ink bg-ink p-11 text-cream shadow-neo-lg shadow-periwinkle">
        <h2 className="mb-4 font-display text-4xl">Let&apos;s build something reliable.</h2>
        <p className="mb-[26px] text-base leading-relaxed text-[#D8D2C6]">
          Open to full-time roles, consulting, and infrastructure audits. Or just grab my resume.
        </p>
        <button
          type="button"
          onClick={onResumeClick}
          className="neo-btn inline-flex items-center gap-2.5 rounded-[14px] border-yellow bg-yellow px-[26px] py-3.5 text-base text-ink"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M12 3v13" stroke="#141414" strokeWidth="2" strokeLinecap="round" />
            <path
              d="M7 11l5 5 5-5"
              stroke="#141414"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path d="M4 20h16" stroke="#141414" strokeWidth="2" strokeLinecap="round" />
          </svg>
          Download resume
        </button>
      </div>

      <form
        onSubmit={handleSubmit}
        noValidate
        className="flex flex-col gap-4 rounded-3xl border-3 border-ink bg-white p-11 shadow-neo-lg"
      >
        {status === "sent" ? (
          <div className="flex h-full flex-col items-start justify-center gap-3">
            <p className="text-[36px]">🤝</p>
            <h3 className="font-display text-2xl text-ink">Message sent!</h3>
            <p className="text-base leading-relaxed text-muted">
              Thanks {form.name || "for reaching out"}, your message is in my inbox. I&apos;ll get
              back to you soon.
            </p>
            <button
              type="button"
              onClick={() => setStatus("idle")}
              className="mt-2 text-sm font-bold text-teal underline"
            >
              Send another
            </button>
          </div>
        ) : (
          <>
            <input
              type="text"
              placeholder="Your name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="neo-input"
              disabled={status === "sending"}
              required
            />
            <input
              type="email"
              placeholder="Your email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="neo-input"
              disabled={status === "sending"}
              required
            />
            <textarea
              placeholder="What are you building?"
              rows={4}
              value={form.message}
              onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              className="neo-input resize-none"
              disabled={status === "sending"}
              required
            />

            {error && (
              <p className="rounded-lg border-2 border-pink bg-pink/10 px-3 py-2 text-sm font-semibold text-pink">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={status === "sending"}
              className="neo-btn rounded-[14px] bg-yellow px-[26px] py-3.5 text-base text-ink disabled:cursor-not-allowed disabled:opacity-70"
            >
              {status === "sending" ? "Sending…" : "Send message"}
            </button>
          </>
        )}
      </form>
    </section>
  );
}
